import { useMemo, type ReactNode } from "react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { SurchargeCode } from "@/components/common/SurchargeCode";
import { Badge } from "@/components/ui/badge";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes, formatLaneKey } from "@/lib/formatPort";
import type {
  KaiRateCardDiff,
  KaiRateCardDiffLine,
  KaiRateCardOverweightPolicy,
  KaiRateCardOwnership,
  KaiRateCardPolicy,
  KaiRateCardSurchargeDerivation,
} from "./kaiTypes";

/**
 * Validate an untyped elicitation preview payload as a `rate_card_diff`.
 * Returns `null` on any shape mismatch so the caller can fall back to parsing
 * the text preview.
 */
export function parseRateCardDiff(payload: unknown): KaiRateCardDiff | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  if (!Array.isArray(p.lines) || p.lines.length === 0) return null;
  for (const line of p.lines) {
    if (typeof line?.route !== "string") return null;
  }
  return {
    header: Array.isArray(p.header) ? (p.header as KaiRateCardDiff["header"]) : [],
    lines: p.lines as KaiRateCardDiffLine[],
    diff:
      p.diff && typeof p.diff === "object"
        ? (p.diff as KaiRateCardDiff["diff"])
        : { added: (p.lines as unknown[]).length },
    ownership:
      p.ownership && typeof p.ownership === "object"
        ? (p.ownership as KaiRateCardOwnership)
        : undefined,
    is_spot: typeof p.is_spot === "boolean" ? p.is_spot : undefined,
    policies: Array.isArray(p.policies)
      ? (p.policies as KaiRateCardPolicy[])
      : undefined,
    overweightPolicies: Array.isArray(p.overweight_policies)
      ? (p.overweight_policies as KaiRateCardOverweightPolicy[])
      : undefined,
    warnings: Array.isArray(p.warnings)
      ? (p.warnings.filter((w) => typeof w === "string") as string[])
      : undefined,
    surchargeDerivation:
      p.surcharge_derivation && typeof p.surcharge_derivation === "object"
        ? (p.surcharge_derivation as KaiRateCardSurchargeDerivation)
        : undefined,
    overweightDerivation:
      p.overweight_derivation && typeof p.overweight_derivation === "object"
        ? (p.overweight_derivation as KaiRateCardDiff["overweightDerivation"])
        : undefined,
    freeDaysSummary:
      typeof p.free_days_summary === "string" ? p.free_days_summary : undefined,
    line_count:
      typeof p.line_count === "number"
        ? p.line_count
        : (p.lines as unknown[]).length,
  };
}

function headerLabelKey(label: string): string {
  return label.trim().toLowerCase();
}

/** Collapse separate valid-from / valid-to header cells into one validity range. */
export function compactRateCardDiffHeader(
  header: { label: string; value: string }[],
): { label: string; value: string }[] {
  let validFrom: string | undefined;
  let validTo: string | undefined;
  const out: { label: string; value: string }[] = [];

  for (const h of header) {
    const key = headerLabelKey(h.label);
    if (key === "valid from") {
      validFrom = h.value;
      continue;
    }
    if (key === "valid to") {
      validTo = h.value;
      continue;
    }
    out.push(h);
  }

  if (validFrom == null && validTo == null) return out;

  const value =
    validFrom && validTo
      ? `${validFrom} → ${validTo}`
      : (validFrom ?? validTo ?? "—");

  const sourceIdx = out.findIndex((h) => headerLabelKey(h.label) === "source");
  const insertAt = sourceIdx >= 0 ? sourceIdx + 1 : out.length;
  out.splice(insertAt, 0, { label: "Validity", value });

  return out;
}

export function RateCardHeaderValue({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  if (headerLabelKey(label) === "carrier") {
    return <CarrierLogo carrier={value} size={16} bold className="text-sm" />;
  }
  return <span className="text-sm text-foreground">{value}</span>;
}

export function formatRateCardDiffRoute(
  route: string,
  showLocodes: boolean,
  portNames: ReadonlyMap<string, string>,
): string {
  return formatLaneKey(route, showLocodes, portNames);
}

function RateCardDiffLinesTable({ diff }: { diff: KaiRateCardDiff }) {
  const { preferences } = useUserPreferences();
  const locodes = useMemo(
    () => extractLaneLocodes(...diff.lines.map((line) => line.route)),
    [diff.lines],
  );
  const portNames = usePortNames(locodes);

  return (
    <div className="overflow-x-auto rounded-lg border border-border/50">
      <table className="w-full border-collapse text-left text-[12px]">
        <thead>
          <tr className="border-b border-border/50 bg-foreground/[0.04] text-muted-foreground">
            <th className="px-2 py-1.5 font-medium">#</th>
            <th className="px-2 py-1.5 font-medium">Lane</th>
            <th className="px-2 py-1.5 font-medium">Equipment</th>
            <th className="px-2 py-1.5 font-medium">FRT</th>
            <th className="px-2 py-1.5 font-medium">Surcharges</th>
            <th className="px-2 py-1.5 font-medium">All-in</th>
            {diff.freeDaysSummary && (
              <th className="px-2 py-1.5 font-medium">Free days</th>
            )}
          </tr>
        </thead>
        <tbody>
          {diff.lines.map((line, i) => (
            <tr key={i} className="border-b border-border/30 last:border-b-0">
              <td className="px-2 py-1.5 tabular-nums">{line.idx}</td>
              <td className="px-2 py-1.5">
                {formatRateCardDiffRoute(
                  line.route,
                  preferences.showLocodes,
                  portNames,
                )}
              </td>
              <td className="px-2 py-1.5">{line.equipment}</td>
              <td className="px-2 py-1.5 tabular-nums">{line.frt}</td>
              <td className="px-2 py-1.5">{line.surcharges}</td>
              <td className="px-2 py-1.5 tabular-nums">{line.all_in}</td>
              {diff.freeDaysSummary && (
                <td className="px-2 py-1.5 tabular-nums">
                  {line.free_days != null ? line.free_days : "—"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InfoAlert({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-sky-500/30 bg-sky-500/8 px-3 py-2 text-xs">
      <p className="font-medium text-foreground">{title}</p>
      {children && <div className="mt-1 text-muted-foreground">{children}</div>}
    </div>
  );
}

/**
 * Structured rate-card diff preview shown inside an elicitation confirmation.
 * A typed line table plus a summary of how many lines the card adds.
 */
export function KaiRateCardDiffCard({ diff }: { diff: KaiRateCardDiff }) {
  const header = compactRateCardDiffHeader(diff.header);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5">
        {diff.ownership && (
          <Badge variant={diff.ownership.type === "Prepaid" ? "urgent" : "secondary"}>
            {diff.ownership.label}
          </Badge>
        )}
        {diff.is_spot && <Badge variant="info">Spot</Badge>}
        {diff.diff.added > 0 && (
          <Badge variant="default">{diff.diff.added} new lines</Badge>
        )}
        {diff.diff.changed != null && diff.diff.changed > 0 && (
          <Badge variant="urgent">{diff.diff.changed} changed</Badge>
        )}
      </div>

      {header.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {header.map((h, i) => (
            <div key={i} className="flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {h.label}
              </span>
              <RateCardHeaderValue label={h.label} value={h.value} />
            </div>
          ))}
        </div>
      )}

      {diff.warnings && diff.warnings.length > 0 && (
        <div className="flex flex-col gap-1">
          {diff.warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-600 dark:text-amber-400">
              {w}
            </p>
          ))}
        </div>
      )}

      {diff.surchargeDerivation && (
        <InfoAlert title={diff.surchargeDerivation.label}>
          <p>{diff.surchargeDerivation.note}</p>
          {diff.surchargeDerivation.codes_applied.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {diff.surchargeDerivation.codes_applied.map((code) => (
                <Badge key={code} variant="urgent" className="text-[10px]">
                  <SurchargeCode code={code} />
                </Badge>
              ))}
            </div>
          )}
        </InfoAlert>
      )}

      {diff.overweightDerivation && (
        <InfoAlert title={diff.overweightDerivation.label}>
          {diff.overweightDerivation.note}
        </InfoAlert>
      )}

      {diff.overweightPolicies && diff.overweightPolicies.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Overweight policies
          </span>
          {diff.overweightPolicies.map((pol, i) => (
            <p key={i} className="text-xs text-foreground/80">
              {pol.scope} · {pol.equipment} · {pol.tiers.join(", ")}
            </p>
          ))}
        </div>
      )}

      {diff.freeDaysSummary && (
        <InfoAlert title="Free days → linked D&D policies">
          The free days ({diff.freeDaysSummary}) will create linked
          demurrage/detention policies scoped to each line's lane and equipment
          when the card is saved.
        </InfoAlert>
      )}

      {diff.policies && diff.policies.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Policies
          </span>
          {diff.policies.map((pol, i) => (
            <div
              key={i}
              className="flex items-center gap-1 text-xs text-foreground/80"
            >
              <Badge variant="outline" className="text-[10px]">
                {pol.label}
              </Badge>
              <span>{pol.value}</span>
            </div>
          ))}
        </div>
      )}

      <RateCardDiffLinesTable diff={diff} />
    </div>
  );
}
